/**
 * Epic 278 Slice B/D — client-side Attention derivation from safety-dashboard inventory.
 * Work-state labels come from Slice D shared resolver (`workspaceResolvedState` /
 * server `customer_*` fields). TODO(epic278): lightweight attention list API later.
 */

import { ATTENTION_METRIC_LABELS } from './safetyCasesWorkspacePresentationCopy';
import { isAwaitingSignoffState } from './workspaceResolvedState';
import { isWorkspaceDueDateOverdue } from './workspaceDueDates';
import {
  INVESTIGATE_LABEL,
  OPEN_SAFETY_CASE_LABEL,
  REVIEW_SAFETY_SCAN_LABEL,
  attentionBuyerPriorityRank,
  dedupeManagedAttentionItems,
} from './managedSafetyBuyerUx';
import { buyerConditionDisplayName, buyerOwnerDisplayName } from '../buyerFacing/conditionDisplayNames';

export type AttentionMetricKey =
  | 'needs_review'
  | 'active_cases'
  | 'awaiting_signoff'
  | 'new_activity'
  | 'coverage_run'
  | 'overdue'
  | 'unowned';

export type AttentionItemType =
  | 'risk_pattern_review'
  | 'active_safety_case'
  | 'awaiting_signoff'
  | 'new_activity'
  | 'coverage_run';

export type AttentionActionKind =
  | 'review_pattern'
  | 'open_case'
  | 'review_activity'
  | 'sign_scan'
  | 'resolve_coverage'
  | 'assign_owner'
  | 'open_site_workspace';

export type WorkspaceAttentionItem = {
  id: string;
  metricKey: AttentionMetricKey;
  itemType: AttentionItemType;
  priority: 'high' | 'medium' | 'low';
  priorityRank: number;
  itemLabel: string;
  /** Display-only risk/issue label; itemLabel remains the canonical title. */
  issueLabel?: string | null;
  evidence: string;
  location: string;
  workState: string;
  owner: string;
  /** Stable login/account identity; never a composed display label. */
  ownerIdentity?: string | null;
  updatedAt: string | null;
  dueAt: string | null;
  isOverdue: boolean;
  isUnowned: boolean;
  actionLabel: string;
  actionKind: AttentionActionKind;
  sourceKey: string;
  workflowId?: string | null;
  reportId?: string | null;
  safetyCaseId?: string | null;
  siteId?: string | null;
  regionLabel?: string | null;
  siteOwner?: string | null;
};

export type WorkspaceAttentionMetric = {
  key: AttentionMetricKey;
  label: string;
  count: number;
  secondary: string;
};

export type AttentionSourceRow = {
  workflowId?: string | null;
  reportId?: string | null;
  safetyCaseId?: string | null;
  title?: string | null;
  riskLabel?: string | null;
  areaLabel?: string | null;
  zoneLabel?: string | null;
  siteLabel?: string | null;
  /** Physical site/area/mount path; preferred over CV zone / policy labels. */
  locationPath?: string | null;
  locationSecondary?: string | null;
  owner?: string | null;
  ownerIdentity?: string | null;
  updatedAt?: string | null;
  dueAt?: string | null;
  observationCount?: number | null;
  newActivityCount?: number | null;
  impactMeasurementStatus?: string | null;
  recommendedNextStep?: string | null;
  blockerCodes?: string[];
  scanStatusKind?: 'signed' | 'draft' | 'awaiting_signoff' | string | null;
  /** Server-resolved Scan workflow state (Slice D) — preferred over scanStatusKind. */
  customerScanWorkflowState?: string | null;
  customerAttentionWorkState?: string | null;
};

export type AttentionBuildInput = {
  candidates: AttentionSourceRow[];
  safetyCases: AttentionWorkGroup[];
  /** Scan-only work groups, including technical anchors that are not managed Safety Cases. */
  scanWorkItems?: AttentionWorkGroup[];
};

export type AttentionWorkGroup = {
  key: string;
  title: string;
  issueLabel?: string | null;
  owner?: string | null;
  ownerIdentity?: string | null;
  zoneLabel?: string | null;
  areaLabel?: string | null;
  updatedAt?: string | null;
  dueAt?: string | null;
  hasNewActivity: boolean;
  newActivityCount?: number | null;
  reviewState?: string | null;
  primary: AttentionSourceRow;
  awaitingScans: AttentionSourceRow[];
  /** Current Scan report_id for managed Cases. Verify tasks use this Scan only. */
  currentReportId?: string | null;
};

const METRIC_LABELS: Record<AttentionMetricKey, string> = { ...ATTENTION_METRIC_LABELS };

const ATTENTION_PARAM = 'attention';

export function resolveAttentionFilter(raw: string | null | undefined): AttentionMetricKey | null {
  const value = String(raw || '').trim().toLowerCase();
  if ((Object.keys(METRIC_LABELS) as AttentionMetricKey[]).includes(value as AttentionMetricKey)) {
    return value as AttentionMetricKey;
  }
  return null;
}

export function attentionFilterParam(): string {
  return ATTENTION_PARAM;
}

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function resolveIssueLabel(title: string, issue?: string | null, riskLabel?: string | null): string {
  return buyerConditionDisplayName(String(issue || riskLabel || title || '').trim() || String(title || '').trim());
}

export function attentionItemDisplayLabel(item: Pick<WorkspaceAttentionItem, 'itemLabel' | 'issueLabel'>): {
  primary: string;
  canonicalTitle: string | null;
} {
  const canonical = String(item.itemLabel || '').trim();
  const primary = buyerConditionDisplayName(String(item.issueLabel || canonical).trim() || canonical);
  const mappedCanonical = canonical ? buyerConditionDisplayName(canonical) : '';
  return {
    primary,
    canonicalTitle: mappedCanonical && mappedCanonical.toLowerCase() !== primary.toLowerCase()
      ? canonical
      : null,
  };
}

type ScanOnlyAttentionRow = {
  workflow_id?: string | null;
  report_id?: string | null;
  active_draft_report_id?: string | null;
  area_label?: string | null;
  site_label?: string | null;
  site_id?: string | null;
  updated_at?: string | null;
  impact_measurement_status?: string | null;
  recommended_next_step?: string | null;
  blockers?: Array<{ code?: unknown }> | null;
  customer_scan_workflow_state?: string | null;
  customer_attention_work_state?: string | null;
};

type ScanOnlyAttentionScan<Row extends ScanOnlyAttentionRow> = {
  reportId: string | null;
  statusKind: AttentionSourceRow['scanStatusKind'];
  actionRow: Row;
};

type ScanOnlyAttentionGroup<Row extends ScanOnlyAttentionRow, Scan extends ScanOnlyAttentionScan<Row>> = {
  key: string;
  title: string;
  originatingRiskPattern?: string | null;
  owner?: string | null;
  ownerIdentity?: string | null;
  zoneLabel?: string | null;
  latestObservationAt?: string | null;
  primaryRow: Row;
  scanHistory: Scan[];
};

type ScanOnlyAttentionBuildParams<
  Row extends ScanOnlyAttentionRow,
  Scan extends ScanOnlyAttentionScan<Row>,
  Lookups,
> = {
  safetyCaseGroups: Array<ScanOnlyAttentionGroup<Row, Scan>>;
  locationLookups: Lookups;
  resolvePortfolioPhysicalLocation: (
    row: Row,
    lookups: Lookups,
    opts?: { zoneLabel?: string | null; cameraName?: string | null },
  ) => { primary: string; secondary: string };
  resolveAttentionDueAt: (row: Row) => string | null;
  resolveCaseOwner: (row: Row) => string | null;
  resolveCaseOwnerIdentity: (row: Row) => string | null;
  resolveScanStatus: (row: Row) => { kind: AttentionSourceRow['scanStatusKind'] };
  resolveSafetyCaseId: (row: Row) => string | null | undefined;
  resolveCameraLabel: (row: Row) => string | null;
  resolveZoneLabel: (row: Row, lookups: Lookups) => string | null;
};

export function buildScanOnlyAttentionWorkItems<
  Row extends ScanOnlyAttentionRow,
  Scan extends ScanOnlyAttentionScan<Row>,
  Lookups,
>({
  safetyCaseGroups,
  locationLookups,
  resolvePortfolioPhysicalLocation,
  resolveAttentionDueAt,
  resolveCaseOwner,
  resolveCaseOwnerIdentity,
  resolveScanStatus,
  resolveSafetyCaseId,
  resolveCameraLabel,
  resolveZoneLabel,
}: ScanOnlyAttentionBuildParams<Row, Scan, Lookups>): AttentionWorkGroup[] {
  return safetyCaseGroups.map((safetyCase) => {
    const primaryLoc = resolvePortfolioPhysicalLocation(safetyCase.primaryRow, locationLookups, {
      zoneLabel: safetyCase.zoneLabel,
      cameraName: resolveCameraLabel(safetyCase.primaryRow),
    });
    return {
      key: safetyCase.key,
      title: safetyCase.title,
      issueLabel: safetyCase.originatingRiskPattern,
      owner: safetyCase.owner,
      ownerIdentity: safetyCase.ownerIdentity,
      zoneLabel: safetyCase.zoneLabel,
      areaLabel: safetyCase.primaryRow.area_label,
      updatedAt: safetyCase.latestObservationAt || safetyCase.primaryRow.updated_at,
      dueAt: resolveAttentionDueAt(safetyCase.primaryRow),
      hasNewActivity: false,
      currentReportId: safetyCase.primaryRow.active_draft_report_id || safetyCase.primaryRow.report_id || null,
      primary: {
        workflowId: safetyCase.primaryRow.workflow_id,
        reportId: safetyCase.primaryRow.report_id || safetyCase.primaryRow.active_draft_report_id,
        safetyCaseId: resolveSafetyCaseId(safetyCase.primaryRow),
        areaLabel: safetyCase.primaryRow.area_label,
        zoneLabel: safetyCase.zoneLabel,
        siteLabel: safetyCase.primaryRow.site_label || safetyCase.primaryRow.site_id,
        locationPath: primaryLoc.primary,
        locationSecondary: primaryLoc.secondary,
        owner: safetyCase.owner,
        ownerIdentity: safetyCase.ownerIdentity,
        updatedAt: safetyCase.primaryRow.updated_at,
        dueAt: resolveAttentionDueAt(safetyCase.primaryRow),
        impactMeasurementStatus: safetyCase.primaryRow.impact_measurement_status,
        recommendedNextStep: safetyCase.primaryRow.recommended_next_step,
        blockerCodes: (safetyCase.primaryRow.blockers || []).map((blocker) => String(blocker.code || '')),
        scanStatusKind: resolveScanStatus(safetyCase.primaryRow).kind,
        customerScanWorkflowState: safetyCase.primaryRow.customer_scan_workflow_state,
        customerAttentionWorkState: safetyCase.primaryRow.customer_attention_work_state,
      },
      awaitingScans: safetyCase.scanHistory
        .filter((scan) => scan.statusKind === 'draft' || scan.statusKind === 'awaiting_signoff')
        .map((scan) => {
          const scanLoc = resolvePortfolioPhysicalLocation(scan.actionRow, locationLookups, {
            zoneLabel: resolveZoneLabel(scan.actionRow, locationLookups),
            cameraName: resolveCameraLabel(scan.actionRow),
          });
          return {
            workflowId: scan.actionRow.workflow_id,
            reportId: scan.reportId,
            areaLabel: scan.actionRow.area_label,
            zoneLabel: resolveZoneLabel(scan.actionRow, locationLookups),
            siteLabel: scan.actionRow.site_label || scan.actionRow.site_id,
            locationPath: scanLoc.primary,
            locationSecondary: scanLoc.secondary,
            owner: resolveCaseOwner(scan.actionRow) || safetyCase.owner,
            ownerIdentity: resolveCaseOwnerIdentity(scan.actionRow) || safetyCase.ownerIdentity,
            updatedAt: scan.actionRow.updated_at,
            dueAt: resolveAttentionDueAt(scan.actionRow),
            impactMeasurementStatus: scan.actionRow.impact_measurement_status,
            recommendedNextStep: scan.actionRow.recommended_next_step,
            blockerCodes: (scan.actionRow.blockers || []).map((blocker) => String(blocker.code || '')),
            scanStatusKind: scan.statusKind,
            customerScanWorkflowState: scan.statusKind === 'signed' ? 'Signed' : (scan.actionRow.customer_scan_workflow_state || null),
            customerAttentionWorkState: scan.actionRow.customer_attention_work_state || null,
          };
        }),
    };
  });
}

function locationOf(row: AttentionSourceRow, fallbackZone?: string | null, fallbackArea?: string | null): string {
  const physical = String(row.locationPath || '').trim();
  const secondary = String(row.locationSecondary || '').trim();
  if (physical) {
    return secondary && secondary.toLowerCase() !== physical.toLowerCase()
      ? `${physical} · ${secondary}`
      : physical;
  }
  // Never promote policy placeholders / bare CV zone slugs as Location alone.
  const candidates = [
    row.areaLabel,
    fallbackArea,
    row.zoneLabel,
    fallbackZone,
    row.siteLabel,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  for (const candidate of candidates) {
    const lower = candidate.toLowerCase();
    if (
      lower === 'configured restricted area'
      || lower === 'configured restricted zone'
      || lower === 'monitored zone'
      || lower === 'selected area'
      || /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/i.test(candidate)
    ) {
      continue;
    }
    return candidate;
  }
  return candidates[0] || 'Location not recorded';
}

function ownerOf(row: AttentionSourceRow, fallback?: string | null): string {
  return buyerOwnerDisplayName(row.owner || fallback);
}

function isUnownedWork(owner: string, ownerIdentity?: string | null): boolean {
  if (String(ownerIdentity || '').trim()) return false;
  const label = normalize(owner).replace(/\s+/g, ' ');
  return !label || label === 'unassigned' || label === 'not assigned' || label === 'not recorded' || label === '—' || label === '-';
}

function isOverdueWork(dueAt?: string | null): boolean {
  return isWorkspaceDueDateOverdue(dueAt);
}

/** Open age in whole days from updatedAt (UTC); null when unknown. */
export function attentionOpenAgeDays(
  updatedAt?: string | null,
  nowMs: number = Date.now(),
): number | null {
  if (!updatedAt) return null;
  const ms = new Date(updatedAt).getTime();
  if (!Number.isFinite(ms)) return null;
  const days = Math.floor((nowMs - ms) / 86400000);
  return days < 0 ? 0 : days;
}

/** Compact age label for Attention rows, e.g. Pending 28d. */
export function formatAttentionOpenAgeLabel(
  updatedAt?: string | null,
  nowMs: number = Date.now(),
): string | null {
  const days = attentionOpenAgeDays(updatedAt, nowMs);
  if (days == null) return null;
  if (days <= 0) return 'Pending today';
  if (days === 1) return 'Pending 1d';
  return `Pending ${days}d`;
}

/**
 * Buyer Attention sort (frontend presentation only):
 * blocking/error → current Scan verification → owner/action required
 * → Control/follow-up → managed new activity → monitoring/no action,
 * then overdue, then oldest open age, then oldest updated.
 */
export function compareAttentionItems(
  left: WorkspaceAttentionItem,
  right: WorkspaceAttentionItem,
  nowMs: number = Date.now(),
): number {
  const leftPri = attentionBuyerPriorityRank(left);
  const rightPri = attentionBuyerPriorityRank(right);
  if (leftPri !== rightPri) return leftPri - rightPri;
  if (left.isOverdue !== right.isOverdue) return left.isOverdue ? -1 : 1;
  const leftAge = attentionOpenAgeDays(left.updatedAt, nowMs);
  const rightAge = attentionOpenAgeDays(right.updatedAt, nowMs);
  const leftAgeKey = leftAge == null ? -1 : leftAge;
  const rightAgeKey = rightAge == null ? -1 : rightAge;
  if (leftAgeKey !== rightAgeKey) return rightAgeKey - leftAgeKey;
  return String(left.updatedAt || '').localeCompare(String(right.updatedAt || ''));
}

export function sortWorkspaceAttentionItems(
  items: WorkspaceAttentionItem[],
  nowMs: number = Date.now(),
): WorkspaceAttentionItem[] {
  return [...items].sort((left, right) => compareAttentionItems(left, right, nowMs));
}

export type AttentionSummaryStripModel = {
  oldestOpenDays: number | null;
  needsReview: number;
  unowned: number;
  newActivity: number;
  segments: Array<{
    key: AttentionMetricKey | 'oldest_open';
    label: string;
    filterKey: AttentionMetricKey | null;
  }>;
  text: string;
};

/** One-line Attention summary replacing seven KPI cards. */
export function buildAttentionSummaryStrip(
  metrics: WorkspaceAttentionMetric[],
  items: WorkspaceAttentionItem[],
  nowMs: number = Date.now(),
): AttentionSummaryStripModel {
  const byKey = new Map(metrics.map((metric) => [metric.key, metric.count]));
  const needsReview = byKey.get('needs_review') ?? 0;
  const unowned = byKey.get('unowned') ?? 0;
  const newActivity = byKey.get('new_activity') ?? 0;
  let oldestOpenDays: number | null = null;
  for (const item of items) {
    const days = attentionOpenAgeDays(item.updatedAt, nowMs);
    if (days == null) continue;
    if (oldestOpenDays == null || days > oldestOpenDays) oldestOpenDays = days;
  }
  const metricSegments = metrics.map((metric) => ({
    key: metric.key as AttentionSummaryStripModel['segments'][number]['key'],
    label: `${metric.count} ${metric.label}`,
    filterKey: metric.key,
  }));
  const segments: AttentionSummaryStripModel['segments'] = [
    ...metricSegments,
  ];
  return {
    oldestOpenDays,
    needsReview,
    unowned,
    newActivity,
    segments,
    text: [`${needsReview} need review`, `${unowned} unowned`, `${newActivity} with new activity`].join(' · '),
  };
}

function withIntelligence(item: Omit<WorkspaceAttentionItem, 'isOverdue' | 'isUnowned'>): WorkspaceAttentionItem {
  return {
    ...item,
    isOverdue: isOverdueWork(item.dueAt),
    isUnowned: isUnownedWork(item.owner, item.ownerIdentity),
  };
}

/** Hard coverage / run blockers that must win over awaiting sign-off. */
export function isHardCoverageOrRunIssue(row: AttentionSourceRow): boolean {
  const attention = String(row.customerAttentionWorkState || '').trim();
  const scanState = String(row.customerScanWorkflowState || '').trim();
  if (attention === 'Insufficient coverage' || scanState === 'Insufficient coverage' || scanState === 'Failed') {
    return true;
  }
  const step = normalize(row.recommendedNextStep);
  if (step === 'complete_impact_coverage' || step === 'resolve_coverage_issue') return true;
  const impact = normalize(row.impactMeasurementStatus);
  if (impact.includes('insufficient') || (impact.includes('coverage') && impact !== 'pending')) {
    return true;
  }
  return (row.blockerCodes || []).some((code) => {
    const normalized = normalize(code);
    return (
      normalized === 'complete_impact_coverage'
      || normalized === 'insufficient_coverage'
      || normalized === 'resolve_coverage_issue'
      || (normalized.includes('coverage') && !normalized.includes('measurement_pending'))
    );
  });
}

/**
 * Coverage / measurement signals from dashboard fields (no invented run states).
 * Soft impact-pending alone is a coverage cue when nothing else applies, but
 * report-ready / awaiting sign-off wins over stale impact_measurement_pending.
 */
export function isCoverageOrRunIssue(row: AttentionSourceRow): boolean {
  if (isHardCoverageOrRunIssue(row)) return true;
  const attention = String(row.customerAttentionWorkState || '').trim();
  const scanState = String(row.customerScanWorkflowState || '').trim();
  if (attention === 'Awaiting sign-off' || scanState === 'Awaiting sign-off') return false;
  if (scanState === 'Signed' || attention === 'Signed') return false;
  const impact = normalize(row.impactMeasurementStatus);
  if (impact === 'pending' || impact === 'mixed') return true;
  return (row.blockerCodes || []).some((code) => {
    const normalized = normalize(code);
    return (
      normalized === 'impact_pending'
      || normalized === 'impact_measurement_pending'
      || normalized.includes('measurement')
    );
  });
}

function isAwaitingSignoff(row: AttentionSourceRow): boolean {
  return isAwaitingSignoffState(row.scanStatusKind, row.customerScanWorkflowState);
}

function resolvedAttentionBucket(
  scan: AttentionSourceRow,
  primary: AttentionSourceRow,
): 'awaiting_signoff' | 'coverage_run' | null {
  const attention = String(
    scan.customerAttentionWorkState || primary.customerAttentionWorkState || '',
  ).trim();
  const scanState = String(
    scan.customerScanWorkflowState || primary.customerScanWorkflowState || '',
  ).trim();

  // Slice D customer_* is authoritative when present (Signed excluded from awaitingScans).
  if (attention === 'Awaiting sign-off' || scanState === 'Awaiting sign-off') return 'awaiting_signoff';
  if (
    attention === 'Insufficient coverage'
    || scanState === 'Insufficient coverage'
    || scanState === 'Failed'
    || attention === 'Failed'
  ) {
    return 'coverage_run';
  }

  // Unsigned-ready / awaiting sign-off wins over stale soft impact-pending.
  const awaiting = isAwaitingSignoff(scan) || isAwaitingSignoff(primary);
  if (awaiting && !isHardCoverageOrRunIssue(scan) && !isHardCoverageOrRunIssue(primary)) {
    return 'awaiting_signoff';
  }
  if (isCoverageOrRunIssue(scan) || isCoverageOrRunIssue(primary)) return 'coverage_run';
  if (awaiting) return 'awaiting_signoff';
  return null;
}

function attentionWorkState(row: AttentionSourceRow, fallback: string): string {
  return String(row.customerAttentionWorkState || fallback).trim() || fallback;
}

function formatRelativeAge(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const days = Math.max(0, Math.floor((Date.now() - ms) / 86400000));
  if (days <= 0) return 'updated today';
  if (days === 1) return 'oldest pending: 1 day';
  return `oldest pending: ${days} days`;
}

export function buildWorkspaceAttention(input: AttentionBuildInput): {
  phase1Items: WorkspaceAttentionItem[];
  activeCaseItems: WorkspaceAttentionItem[];
  metrics: WorkspaceAttentionMetric[];
} {
  const phase1Items: WorkspaceAttentionItem[] = [];
  const seen = new Set<string>();

  const push = (item: WorkspaceAttentionItem) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    phase1Items.push(item);
  };

  input.candidates.forEach((row, index) => {
    const sourceKey = String(row.workflowId || `candidate-${index}`).trim();
    const count = row.observationCount;
    push(withIntelligence({
      id: `needs_review:${sourceKey}`,
      metricKey: 'needs_review',
      itemType: 'risk_pattern_review',
      priority: 'medium',
      priorityRank: attentionBuyerPriorityRank({ metricKey: 'needs_review', actionKind: 'review_pattern' }),
      itemLabel: String(row.title || row.riskLabel || 'Risk Pattern').trim(),
      issueLabel: resolveIssueLabel(
        String(row.title || row.riskLabel || 'Risk Pattern').trim(),
        null,
        row.riskLabel,
      ),
      evidence: typeof count === 'number' && Number.isFinite(count)
        ? `${count} observation${count === 1 ? '' : 's'}`
        : 'Review required',
      location: locationOf(row),
      workState: attentionWorkState(row, 'Review required'),
      owner: ownerOf(row),
      ownerIdentity: row.ownerIdentity || null,
      updatedAt: row.updatedAt || null,
      dueAt: row.dueAt || null,
      actionLabel: INVESTIGATE_LABEL,
      actionKind: 'review_pattern',
      sourceKey,
      workflowId: sourceKey,
    }));
  });

  const currentScanRows = (safetyCase: AttentionWorkGroup): AttentionSourceRow[] => {
    const currentId = String(
      safetyCase.currentReportId
      || safetyCase.primary.reportId
      || '',
    ).trim();
    if (currentId) {
      const matched = safetyCase.awaitingScans.filter(
        (scan) => String(scan.reportId || '').trim() === currentId,
      );
      if (matched.length) return matched;
    }
    return safetyCase.awaitingScans.slice(0, 1);
  };

  const pushScanAttention = (safetyCase: AttentionWorkGroup) => {
    currentScanRows(safetyCase).forEach((scan, scanIndex) => {
      const sourceKey = String(scan.reportId || scan.workflowId || `${safetyCase.key}:scan:${scanIndex}`).trim();
      const bucket = resolvedAttentionBucket(scan, safetyCase.primary);
      if (bucket === 'coverage_run') {
        push(withIntelligence({
          id: `coverage_run:${safetyCase.key}`,
          metricKey: 'coverage_run',
          itemType: 'coverage_run',
          priority: 'low',
          priorityRank: attentionBuyerPriorityRank({ metricKey: 'coverage_run', actionKind: 'resolve_coverage' }),
          itemLabel: safetyCase.title,
          issueLabel: resolveIssueLabel(safetyCase.title, safetyCase.issueLabel),
          evidence: 'Insufficient coverage — decision required',
          location: locationOf(scan, safetyCase.zoneLabel, safetyCase.areaLabel),
          workState: attentionWorkState(scan, 'Insufficient coverage'),
          owner: ownerOf(scan, safetyCase.owner),
          ownerIdentity: scan.ownerIdentity || safetyCase.ownerIdentity || null,
          updatedAt: scan.updatedAt || safetyCase.updatedAt || null,
          dueAt: scan.dueAt || safetyCase.dueAt || null,
          actionLabel: 'Resolve coverage issue',
          actionKind: 'resolve_coverage',
          sourceKey,
          workflowId: scan.workflowId || safetyCase.primary.workflowId || null,
          reportId: scan.reportId || null,
          safetyCaseId: safetyCase.key,
        }));
        return;
      }
      if (bucket !== 'awaiting_signoff') return;
      push(withIntelligence({
        id: `awaiting_signoff:${safetyCase.key}`,
        metricKey: 'awaiting_signoff',
        itemType: 'awaiting_signoff',
        priority: 'high',
        priorityRank: attentionBuyerPriorityRank({ metricKey: 'awaiting_signoff', actionKind: 'sign_scan' }),
        itemLabel: safetyCase.title,
        issueLabel: resolveIssueLabel(safetyCase.title, safetyCase.issueLabel),
        evidence: 'Current Safety Scan needs verification',
        location: locationOf(scan, safetyCase.zoneLabel, safetyCase.areaLabel),
        workState: attentionWorkState(scan, 'Awaiting sign-off'),
        owner: ownerOf(scan, safetyCase.owner),
        ownerIdentity: scan.ownerIdentity || safetyCase.ownerIdentity || null,
        updatedAt: scan.updatedAt || safetyCase.updatedAt || null,
        dueAt: scan.dueAt || safetyCase.dueAt || null,
        actionLabel: REVIEW_SAFETY_SCAN_LABEL,
        actionKind: 'sign_scan',
        sourceKey,
        workflowId: scan.workflowId || safetyCase.primary.workflowId || null,
        reportId: scan.reportId || sourceKey,
        safetyCaseId: safetyCase.key,
      }));
    });

    if (
      !safetyCase.awaitingScans.length
      && isHardCoverageOrRunIssue(safetyCase.primary)
    ) {
      push(withIntelligence({
        id: `coverage_run:${safetyCase.key}`,
        metricKey: 'coverage_run',
        itemType: 'coverage_run',
        priority: 'low',
        priorityRank: attentionBuyerPriorityRank({ metricKey: 'coverage_run', actionKind: 'resolve_coverage' }),
        itemLabel: safetyCase.title,
        issueLabel: resolveIssueLabel(safetyCase.title, safetyCase.issueLabel),
        evidence: 'Insufficient coverage — decision required',
        location: locationOf(safetyCase.primary, safetyCase.zoneLabel, safetyCase.areaLabel),
        workState: attentionWorkState(safetyCase.primary, 'Insufficient coverage'),
        owner: ownerOf(safetyCase.primary, safetyCase.owner),
        ownerIdentity: safetyCase.primary.ownerIdentity || safetyCase.ownerIdentity || null,
        updatedAt: safetyCase.updatedAt || safetyCase.primary.updatedAt || null,
        dueAt: safetyCase.primary.dueAt || safetyCase.dueAt || null,
        actionLabel: 'Resolve coverage issue',
        actionKind: 'resolve_coverage',
        sourceKey: safetyCase.key,
        workflowId: safetyCase.primary.workflowId || null,
        safetyCaseId: safetyCase.key,
      }));
    }
  };

  input.safetyCases.forEach((safetyCase) => {
    if (safetyCase.hasNewActivity) {
      const count = safetyCase.newActivityCount;
      push(withIntelligence({
        id: `new_activity:${safetyCase.key}`,
        metricKey: 'new_activity',
        itemType: 'new_activity',
        priority: 'high',
        priorityRank: attentionBuyerPriorityRank({ metricKey: 'new_activity', actionKind: 'open_case' }),
        itemLabel: safetyCase.title,
        issueLabel: resolveIssueLabel(safetyCase.title, safetyCase.issueLabel),
        evidence: typeof count === 'number' && Number.isFinite(count)
          ? `${count} new observation${count === 1 ? '' : 's'} after signed Scan`
          : 'New activity after latest signed Scan',
        location: locationOf(safetyCase.primary, safetyCase.zoneLabel, safetyCase.areaLabel),
        workState: String(safetyCase.reviewState || 'New activity').trim() || 'New activity',
        owner: ownerOf(safetyCase.primary, safetyCase.owner),
        ownerIdentity: safetyCase.primary.ownerIdentity || safetyCase.ownerIdentity || null,
        updatedAt: safetyCase.updatedAt || safetyCase.primary.updatedAt || null,
        dueAt: safetyCase.primary.dueAt || safetyCase.dueAt || null,
        actionLabel: OPEN_SAFETY_CASE_LABEL,
        actionKind: 'open_case',
        sourceKey: safetyCase.key,
        workflowId: safetyCase.primary.workflowId || null,
        safetyCaseId: safetyCase.key,
      }));
    }

    pushScanAttention(safetyCase);
    const alreadyQueued = phase1Items.some((item) => (
      item.safetyCaseId === safetyCase.key || item.sourceKey === safetyCase.key
    ));
    const ownerLabel = ownerOf(safetyCase.primary, safetyCase.owner);
    const unowned = isUnownedWork(ownerLabel, safetyCase.primary.ownerIdentity || safetyCase.ownerIdentity);
    if (!safetyCase.hasNewActivity && unowned && !alreadyQueued) {
      push(withIntelligence({
        id: `unowned:${safetyCase.key}`,
        metricKey: 'unowned',
        itemType: 'active_safety_case',
        priority: 'medium',
        priorityRank: attentionBuyerPriorityRank({ actionKind: 'assign_owner', isUnowned: true }),
        itemLabel: safetyCase.title,
        issueLabel: resolveIssueLabel(safetyCase.title, safetyCase.issueLabel),
        evidence: 'Owner missing',
        location: locationOf(safetyCase.primary, safetyCase.zoneLabel, safetyCase.areaLabel),
        workState: 'Owner missing',
        owner: ownerLabel,
        ownerIdentity: safetyCase.primary.ownerIdentity || safetyCase.ownerIdentity || null,
        updatedAt: safetyCase.updatedAt || safetyCase.primary.updatedAt || null,
        dueAt: safetyCase.primary.dueAt || safetyCase.dueAt || null,
        actionLabel: 'Assign owner',
        actionKind: 'assign_owner',
        sourceKey: safetyCase.key,
        workflowId: safetyCase.primary.workflowId || null,
        safetyCaseId: safetyCase.key,
      }));
    }
  });
  (input.scanWorkItems || []).forEach(pushScanAttention);

  const collapsed = dedupeManagedAttentionItems(phase1Items);
  phase1Items.splice(0, phase1Items.length, ...collapsed);
  phase1Items.sort((left, right) => compareAttentionItems(left, right));

  const activeCaseItems: WorkspaceAttentionItem[] = input.safetyCases.map((safetyCase) => withIntelligence({
    id: `active_cases:${safetyCase.key}`,
    metricKey: 'active_cases' as const,
    itemType: 'active_safety_case' as const,
    priority: 'medium' as const,
    priorityRank: attentionBuyerPriorityRank({ metricKey: 'active_cases', actionKind: 'open_case' }),
    itemLabel: safetyCase.title,
    issueLabel: resolveIssueLabel(safetyCase.title, safetyCase.issueLabel),
    evidence: safetyCase.hasNewActivity ? 'Active · new activity' : 'Active Safety Case',
    location: locationOf(safetyCase.primary, safetyCase.zoneLabel, safetyCase.areaLabel),
    workState: String(safetyCase.reviewState || 'Confirmed').trim() || 'Confirmed',
    owner: ownerOf(safetyCase.primary, safetyCase.owner),
    ownerIdentity: safetyCase.primary.ownerIdentity || safetyCase.ownerIdentity || null,
    updatedAt: safetyCase.updatedAt || safetyCase.primary.updatedAt || null,
    dueAt: safetyCase.primary.dueAt || safetyCase.dueAt || null,
    actionLabel: OPEN_SAFETY_CASE_LABEL,
    actionKind: 'open_case' as const,
    sourceKey: safetyCase.key,
    workflowId: safetyCase.primary.workflowId || null,
    safetyCaseId: safetyCase.key,
  })).sort((left, right) => compareAttentionItems(left, right));

  const operationalItems = [...phase1Items, ...activeCaseItems].sort((left, right) => compareAttentionItems(left, right));

  const countFor = (key: AttentionMetricKey) => (
    key === 'active_cases'
      ? activeCaseItems.length
      : key === 'overdue'
        ? operationalItems.filter((item) => item.isOverdue).length
        : key === 'unowned'
          ? operationalItems.filter((item) => item.isUnowned).length
          : phase1Items.filter((item) => item.metricKey === key).length
  );

  const oldestNeedsReview = phase1Items
    .filter((item) => item.metricKey === 'needs_review')
    .map((item) => item.updatedAt)
    .filter(Boolean)
    .sort()[0] || null;

  const metrics: WorkspaceAttentionMetric[] = (Object.keys(METRIC_LABELS) as AttentionMetricKey[]).map((key) => {
    const count = countFor(key);
    let secondary = 'None requiring action';
    if (key === 'needs_review') secondary = count > 0 ? (formatRelativeAge(oldestNeedsReview) || 'Review required') : 'No patterns awaiting review';
    if (key === 'active_cases') secondary = count > 0 ? `${count} currently managed` : 'No active Safety Cases';
    if (key === 'awaiting_signoff') secondary = count > 0 ? 'Decision required' : 'No scans awaiting sign-off';
    if (key === 'new_activity') secondary = count > 0 ? 'After latest signed Scan' : 'No new activity';
    if (key === 'coverage_run') {
      secondary = count > 0
        ? 'Insufficient coverage'
        : 'No coverage issues'; // scheduled-run FAILED deferred until list API (inventory 4b)
    }
    if (key === 'overdue') secondary = count > 0 ? `${count} past due` : 'No overdue work';
    if (key === 'unowned') secondary = count > 0 ? 'Owner assignment needed' : 'No unowned work';
    return { key, label: METRIC_LABELS[key], count, secondary };
  });

  return { phase1Items, activeCaseItems, metrics };
}

export function filterWorkspaceAttentionItems(
  built: { phase1Items: WorkspaceAttentionItem[]; activeCaseItems: WorkspaceAttentionItem[] },
  filter: AttentionMetricKey | null,
): WorkspaceAttentionItem[] {
  if (filter === 'active_cases') return built.activeCaseItems;
  if (filter === 'overdue') {
    return sortWorkspaceAttentionItems(
      [...built.phase1Items, ...built.activeCaseItems].filter((item) => item.isOverdue),
    );
  }
  if (filter === 'unowned') {
    return sortWorkspaceAttentionItems(
      [...built.phase1Items, ...built.activeCaseItems].filter((item) => item.isUnowned),
    );
  }
  if (!filter) return built.phase1Items;
  return built.phase1Items.filter((item) => item.metricKey === filter);
}

/** Summary count must equal filtered list length for the active metric. */
export function reconcileAttentionMetricCounts(
  built: ReturnType<typeof buildWorkspaceAttention>,
): Array<{ key: AttentionMetricKey; count: number; listLength: number; ok: boolean }> {
  return built.metrics.map((metric) => {
    const listLength = filterWorkspaceAttentionItems(built, metric.key).length;
    return {
      key: metric.key,
      count: metric.count,
      listLength,
      ok: metric.count === listLength,
    };
  });
}
